// src/app/services/geo-location-service.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Common } from '../models/common';
import { AddressByGeoLocation } from '../models/Response/AddressByGeoLocation';
import { Geolocation, PermissionStatus } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GeoLocationService {
  addressInfo: AddressByGeoLocation = {} as AddressByGeoLocation;
  private storageReady = false;

  constructor(private http: HttpClient, private storage: Storage) {
    this.initStorage();
  }

  private async initStorage() {
    await this.storage.create();
    this.storageReady = true;
  }

  /**
   * Cross-platform: get current user location and reverse geocode it.
   */
  async getUserCurrentLocation() {
    try {
      const hasPermission = await this.checkLocationPermission();
      if (!hasPermission) {
        console.warn('Location permission not granted');
        return;
      }

      const platform = Capacitor.getPlatform(); // 'web' | 'android' | 'ios' | ...
      let coords: { latitude: number; longitude: number } | null = null;

      if (platform === 'web') {
        coords = await this.getBrowserPosition();
      } else {
        const pos = await Geolocation.getCurrentPosition();
        coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      }

      if (coords) {
        await this.reverseGeocode(coords.latitude, coords.longitude);
      }
    } catch (error) {
      console.error('Error getting user location', error);
    }
  }

  async searchAddressByText(query: string): Promise<any[]> {

    if (!query || query.trim().length < 3) {
      return [];
    }

    const apiUrl =
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${Common.GOOGLE_MAPS_ANDROID_API_KEY}`;

    try {
      const response: any = await this.http.get(apiUrl).toPromise();

      if (response?.status === 'OK' && response.results?.length) {
        return response.results.slice(0, 3);
      }

      return [];

    } catch (error) {
      console.error('Error searching address', error);
      return [];
    }
  }

  convertGeocodeResultToAddress(result: any): AddressByGeoLocation {

    const components = result.address_components || [];

    return {
      addressId: undefined, // manual search = not saved
      street: result.formatted_address,
      city: this.getComponent(components, 'locality')
        || this.getComponent(components, 'sublocality')
        || '',
      state: this.getComponent(components, 'administrative_area_level_1') || '',
      country: this.getComponent(components, 'country') || '',
      let: Number(result.geometry.location.lat),
      long: Number(result.geometry.location.lng),
      fullAddressInfo: result.formatted_address
    };
  }

  /**
   * Browser getCurrentPosition wrapped in a Promise
   */
  private getBrowserPosition(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocation not available in browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }

  /**
   * Reverse geocode using Google Maps Geocoding API.
   * Make sure Common.GOOGLE_MAPS_ANDROID_API_KEY or server-side key is set appropriately.
   */
  async reverseGeocode(lat: number, lon: number) {

    const apiUrl =
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${Common.GOOGLE_MAPS_ANDROID_API_KEY}`;

    try {
      const response: any = await this.http.get(apiUrl).toPromise();

      if (response?.status === 'OK' && response.results?.length) {

        const result = response.results[0];
        const components = result.address_components;

        const address: AddressByGeoLocation = {
          addressId: undefined,
          street: result.formatted_address,
          city:
            this.getComponent(components, 'locality') ||
            this.getComponent(components, 'sublocality') ||
            '',
          state:
            this.getComponent(components, 'administrative_area_level_1') || '',
          country:
            this.getComponent(components, 'country') || '',
          let: lat,
          long: lon,
          fullAddressInfo: result.formatted_address || ''
        };

        await this.setAddressInfo(address);
      }

    } catch (error) {
      console.error('Error fetching address information', error);
    }
  }

  private getComponent(components: any[], type: string) {
    return components?.find((c) => Array.isArray(c.types) && c.types.indexOf(type) > -1)?.long_name || '';
  }

  async setAddressInfo(address: AddressByGeoLocation) {

    if (!address?.let || !address?.long) {
      console.warn('Invalid address object');
      return;
    }

    this.addressInfo = address;

    if (this.storageReady) {
      await this.storage.set('addressInfo', address);
    }
  }

  async getAddressInfo(): Promise<AddressByGeoLocation | null> {

    if (!this.addressInfo || Object.keys(this.addressInfo).length === 0) {

      if (this.storageReady) {
        const stored = await this.storage.get('addressInfo');
        this.addressInfo = stored || {} as AddressByGeoLocation;
      }
    }

    return this.addressInfo?.let ? this.addressInfo : null;
  }

  /**
   * For development on desktop: mock a fixed location and reverse geocode it.
   */
  async mockLocationForBrowser() {
    const position = {
      coords: {
        latitude: 22.7277906,
        longitude: 75.8640672,
      },
    };
    await this.reverseGeocode(position.coords.latitude, position.coords.longitude);
  }

  /**
   * Cross-platform permission check:
   * - On native use Capacitor Geolocation.checkPermissions / requestPermissions
   * - On web use navigator.permissions (if available) or trigger browser prompt via getCurrentPosition
   */
  async checkLocationPermission(): Promise<boolean> {
    try {
      // alert('Checking location permission');
      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        // Try Permissions API
        try {
          // Some TS libs may not have navigator.permissions typed; guard with optional chaining
          // @ts-ignore
          const perm = await navigator.permissions?.query?.({ name: 'geolocation' } as any);
          if (perm) {
            if (perm.state === 'granted') return true;
            if (perm.state === 'denied') return false;
            // If 'prompt', fall through and call getCurrentPosition to trigger browser prompt
          }
        } catch (_) {
          // ignore - Permissions API unavailable
        }

        // Attempt to trigger permission prompt by requesting position
        return new Promise<boolean>((resolve) => {
          if (!('geolocation' in navigator)) {
            resolve(false);
            return;
          }
          navigator.geolocation.getCurrentPosition(
            () => resolve(true),
            () => resolve(false),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
          );
        });
      } else {
        // alert('Checking location permission on native platform inside else part');
        // Native platforms
        const permStatus: PermissionStatus = await Geolocation.checkPermissions();

        //alert(`Permission status: location=${permStatus.location}, coarseLocation=${permStatus.coarseLocation}`);

        if (permStatus.location === 'granted' || permStatus.coarseLocation === 'granted') {
          // alert('Location permission already granted');
          return true;
        }
        const requestStatus: PermissionStatus = await Geolocation.requestPermissions();
        //alert(`Permission request result: location=${requestStatus.location}, coarseLocation=${requestStatus.coarseLocation}`);
        return (requestStatus.location === 'granted' || requestStatus.coarseLocation === 'granted');
      }
    } catch (err) {
      console.error('Error checking location permission', err);
      return false;
    }
  }
}
